import { Grid } from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormHeading from 'common/FormHeading';
import FormSelectInput from 'common/FormSelectInput';
import FormTextInput from 'common/FormTextInput';
import FormTextInputPhoneMask from 'common/FormTextInputPhoneMask';
import ServiceInputs from 'pages/Payment/Invoice/InvoicePopup/Partials/ServiceInputs';
import PatientNameInput from 'pages/Payment/Invoice/InvoicePopup/Partials/PatientNameInput';
import { theme } from 'theme';

import React, { useMemo } from 'react';

export default function Form({
    formData,
    setFormData,
    handleChange,
    openProviderDropdown,
    setOpenProviderDropdown,
    providers,
    servicesCount,
    setServicesCount,
    invoice,
    servicesList,
    isViewMode,
    business,
    getProviderServices,
    getBusinessServices,
    purchasedService,
    purchaseDate,
    appointmentDate,
    onAddNewServiceClick,
    oooData,
    providerAvailabilityData,
}) {
    const listOfOptions = useMemo(() => {
        return [
            ...providers.map((provider) => ({
                id: provider.id,
                label: `${provider.first_name} ${provider.last_name}`,
                type: 'provider',
            })),
            {
                id: business.id,
                label: business.name,
                type: 'business',
            },
        ];
    }, [providers, business]);

    const handleChangeBusinessProvider = (e) => {
        const { value } = e.target;
        const isBusinessSelected = listOfOptions.find(
            (opt) => Number(opt.id) === Number(value) && Number(opt.id) === business?.id,
        );
        if (isBusinessSelected) {
            setFormData({ ...formData, provider: value, isBusiness: true });
            if (getBusinessServices) {
                getBusinessServices();
            }
        } else {
            setFormData({ ...formData, provider: value, isBusiness: false });
            if (getProviderServices) {
                getProviderServices(value);
            }
        }
    };

    return (
        <>
            <PatientNameInput
                formData={formData}
                setFormData={setFormData}
                disabled={isViewMode}
                required={invoice || (!isViewMode && !purchasedService)}
            />

            {(invoice || purchasedService || (isViewMode && (formData.sendInvoiceByInfo || formData.email))) && (
                <FormTextInput
                    formData={formData}
                    label="Email Address"
                    name="sendInvoiceByInfo"
                    value={formData.sendInvoiceByInfo || formData.email || ''}
                    handleChange={handleChange}
                    placeholder="Enter patient's email address"
                    required={invoice ? true : false}
                    helperText=""
                    disabled={isViewMode}
                    style={{ marginBottom: '8px' }}
                />
            )}

            <Grid item xs={12} sx={{ marginTop: '50px', marginBottom: '12px' }}>
                <Box
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: theme.colorTableHeaderBackground,
                        paddingBottom: '10px',
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <Box
                        sx={{
                            color: theme.secondaryFontColor,
                            fontWeight: 700,
                            fontSize: '18px',
                            fontFamily: 'Poppins'
                        }}
                    >
                        Service Details
                    </Box>
                    {!isViewMode && (
                        <Box
                            className={'primary-link cursor-pointer'}
                            onClick={() => {
                                setServicesCount(servicesCount + 1);
                                // Initialize new service fields - add to the beginning of the array
                                const newServiceName = ['', ...(formData.serviceName || [])];
                                const newServicePrice = ['', ...(formData.servicePrice || [])];
                                setFormData({
                                    ...formData,
                                    serviceName: newServiceName,
                                    servicePrice: newServicePrice
                                });
                            }}
                        >
                            + Add Another Service
                        </Box>
                    )}
                </Box>
            </Grid>
            
            {purchasedService && (
                <Grid item xs={12} sx={{ marginTop: '-12px', marginBottom: '16px' }}>
                    <Typography variant="body2" sx={{ color: '#999', fontSize: '14px', lineHeight: '1.5' }}>
                        Please reach out to the customer to schedule this service independently.
                    </Typography>
                </Grid>
            )}

            {!purchasedService && (
                <>
                    <FormTextInput
                        formData={formData}
                        label="Date of Services"
                        name="dateOfService"
                        handleChange={handleChange}
                        placeholder="Add Date of Service"
                        type="date"
                        required={invoice || (!isViewMode && !purchasedService)}
                        value={formData.dateOfService}
                        startDate={new Date()}
                        invoice={invoice}
                        disabled={isViewMode}
                        style={{ marginBottom: '8px' }}
                    />

                    <FormTextInput
                        formData={formData}
                        label="Time of Services"
                        name="timeOfService"
                        value={formData.timeOfService || ''}
                        handleChange={handleChange}
                        placeholder="Add Time of Service"
                        type={isViewMode ? 'text' : 'time'}
                        required={invoice || (!isViewMode && !purchasedService)}
                        invoice={invoice}
                        disabled={isViewMode}
                        readOnly={!isViewMode}
                        slotsGap={15}
                        oooData={oooData}
                        providerAvailabilityData={providerAvailabilityData}
                        style={{ marginBottom: '8px' }}
                    />
                </>
            )}

            <FormSelectInput
                formData={formData}
                label="Provider or Business"
                name="provider"
                handleChange={handleChangeBusinessProvider}
                placeholder="Select a Provider or Business"
                required={invoice || (!isViewMode && !purchasedService)}
                openDropdown={openProviderDropdown}
                setOpenDropdown={setOpenProviderDropdown}
                renderValue={(selected) => {
                    if (!selected) {
                        return (
                            <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
                                Select a Provider or Business
                            </Typography>
                        );
                    }

                    const selectedItem = listOfOptions?.find((d) => d.id === selected);
                    return selectedItem?.label ?? '';
                }}
                options={listOfOptions}
                optionKey="id"
                optionValue={['label']}
                value={formData.provider}
                disabled={isViewMode}
                style={{ marginBottom: '8px' }}
            />

            {[...Array(servicesCount)].reverse().map((_, reverseIndex) => {
                // When reversed: reverseIndex 0 is the newest (top), reverseIndex (servicesCount-1) is the oldest (bottom)
                // Since new services are added to the beginning of arrays, index 0 is newest, index (servicesCount-1) is oldest
                // So: reverseIndex 0 should map to array index 0 (newest), reverseIndex (servicesCount-1) should map to array index (servicesCount-1) (oldest)
                const index = reverseIndex; // Direct mapping: reverseIndex 0 → array index 0 (newest), etc.
                const serviceNumber = servicesCount - reverseIndex; // Service number for display (newest is highest number)
                return (
                    <ServiceInputs
                        key={index}
                        index={index}
                        formData={formData}
                        setFormData={setFormData}
                        setServicesCount={setServicesCount}
                        servicesList={servicesList}
                        disabled={isViewMode}
                        servicesCount={servicesCount}
                        serviceNumber={serviceNumber}
                        purchasedService={purchasedService}
                        required={invoice || (!isViewMode && !purchasedService)}
                        onAddNewServiceClick={onAddNewServiceClick}
                    />
                );
            })}

            {/* Total Row */}
            <Grid item xs={12} style={{ marginTop: '16px' }}>
                <Box
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: theme.colorTableHeaderBackground,
                        paddingBottom: '10px',
                        marginBottom: '12px',
                        width: '100%'
                    }}
                />
            </Grid>
            <Grid container spacing={0} sx={{ marginTop: '0px' }}>
                <Grid item xs={6} sx={{ paddingLeft: '0px', paddingRight: '0px' }}>
                    <Box
                        sx={{
                            color: theme.secondaryFontColor,
                            fontWeight: 700,
                            fontSize: '18px',
                            fontFamily: 'Poppins',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        Total
                    </Box>
                </Grid>
                <Grid item xs={6} sx={{ paddingLeft: '0px', paddingRight: '0px' }}>
                    <Box
                        sx={{
                            color: theme.secondaryFontColor,
                            fontWeight: 700,
                            fontSize: '18px',
                            fontFamily: 'Poppins',
                            textAlign: 'right',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}
                    >
                        ${(() => {
                            const cleanPrice = (price) => {
                                if (typeof price === 'string') {
                                    return price.replace(/,/g, '');
                                }
                                return price;
                            };
                            const total = (formData?.servicePrice || [])
                                .reduce((sum, price) => {
                                    const cleanedPrice = cleanPrice(price);
                                    const numPrice = parseFloat(cleanedPrice) || 0;
                                    return sum + numPrice;
                                }, 0);
                            return total.toFixed(2);
                        })()}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
}
